import React, { useContext, useEffect, useState, useCallback } from "react";
import { LegacyCard as Card, OptionList, Popover, Select, Tag, ChoiceList, Modal, BlockStack, Text } from "@shopify/polaris";
import { useMutation, useQuery } from "@apollo/client";

// import helpers
import baseHelper from "lib/helpers/base";
import { errorHelper } from "lib/helpers";

// import constant
import constant from "lib/constant/constant";

// import helpers component
import { Banner, ResourceList } from "lib/components";
import FileUpload from "lib/components/fileInput/fileUpload";

// import filter component
import MoreFilters from "app/payments/modules/generic/moreFilters/moreFilter";

// import context
import { PrivateContext } from "lib/context";

// import gql
import ACCOUNT_LIST from "app/payments/apollo/query";
import { PAYMENT_LIST } from "app/payments/apollo/subscriptions";
import { GET_SELLER_SETTING } from "app/setup/apollo/queries";

// import cms
import actionData from "app/payments/modules/operator/features/list/cms/resourceTableData";

// import subFeature
import { listProp } from "app/payments/modules/operator/features/list/propTypes";
import BULK_ACCOUNT from "app/payments/apollo/mutation/bulkAccount";
import { useDebounce } from "lib/hooks";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PaymentRow from "./paymentRow";

const PaymentList = (props) => {
  const { setBanner, bulkModalActive = false, setBulkModalActive } = props;
  const { cms } = useContext(PrivateContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryParam = Object.fromEntries(searchParams.entries());
  const { CRITICAL, DISPUTE, PAYPAL, PAID, STRIPE, gql, PAIDVIAPAYPAL, PAIDVIASTRIPE, SUCCESS, UNPAID } = constant;
  const [loading, setLoading] = useState(true);
  const [taggedWith, setTaggedWith] = useState(queryParam.list_search || "");
  const [selectedFilter, setSelectedFilter] = useState(queryParam.list_filter || "");
  const paramFilter = (queryParam.filter && queryParam.filter.split(",")) || [];
  const [filterByStatus, setFilterByStatus] = useState(paramFilter || []);
  const [paymentListData, setPaymentList] = useState([]);
  const [isExcludeShippingAmount, setIsExcludeShippingAmount] = useState(false);
  const [paymentCount, setPaymentCount] = useState(0);
  const [bulkAccount] = useMutation(BULK_ACCOUNT);
  const resourceData = actionData(cms);
  const [selectedItems, setSelectedItems] = useState([]);
  const [perPage, setPerPage] = useState(parseInt(queryParam.perPage, 10) || 10);
  const [page, setPage] = useState(parseInt(queryParam.page, 10) || 1);
  const [search, setSearch] = useState(queryParam.search || "");
  const sortNameParam = queryParam.sort_name || "createdAt";
  const sortOrderParam = queryParam.sort_order || "desc";
  const [sortValue, setSortValue] = useState(`${sortNameParam}_${sortOrderParam}`);
  const [activePopover, setActivePopover] = useState({});
  const [popoverDisable, setPopoverDisable] = useState(false);
  const [importedIds, setImportedIds] = useState([]);
  const [importFileName, setImportFileName] = useState("");
  const [importError, setImportError] = useState("");
  const [importValues, setImportValues] = useState({});
  const [modalBanner, setModalBanner] = useState({ isOpen: false, title: "", status: "" });

  useEffect(() => {
    if (!importError && modalBanner.isOpen) {
      setModalBanner({ isOpen: false, title: "", status: "" });
    }
  }, [importError, modalBanner.isOpen]);

  const resetImportState = () => {
    setImportedIds([]);
    setImportFileName("");
    setImportError("");
    setImportValues({});
    setModalBanner({ isOpen: false, title: "", status: "" });
  };

  const renderItem = (item) => {
    return (
      <PaymentRow
        isExcludeShippingAmount={isExcludeShippingAmount}
        item={item}
        setBanner={setBanner}
        activePopover={activePopover}
        setActivePopover={setActivePopover}
        popoverDisable={popoverDisable}
      />
    );
  };

  const updatedSearchValue = useDebounce(search, 1000);

  const inputData = {
    filter: filterByStatus && filterByStatus.length ? filterByStatus : paramFilter,
    path: queryParam.path,
    sort_name: sortNameParam,
    sort_order: sortOrderParam,
    sort_date: queryParam.sort_date,
    page: parseInt(queryParam.page, 10),
    perPage: parseInt(perPage, 10),
    list_search: queryParam.list_search,
    list_filter: queryParam.list_filter,
    search: updatedSearchValue,
  };
  const { data: orderLinesData, loading: orderLoading, error, subscribeToMore, refetch } = useQuery(ACCOUNT_LIST, {
    variables: {
      input: inputData,
    },
    notifyOnNetworkStatusChange: true,
  });

  const { data: sellerSetting, loading: sellerSettingLoading } = useQuery(GET_SELLER_SETTING);

  useEffect(() => {
    setSelectedItems([]);
    if (!orderLoading) {
      setLoading(false);
    }
  }, [orderLoading]);

  useEffect(() => {
    if (!queryParam.sort_name || !queryParam.sort_order) {
      baseHelper.setUrl(location, navigate, { sort_name: sortNameParam, sort_order: sortOrderParam });
    }
    setSortValue(`${sortNameParam}_${sortOrderParam}`);
  }, [location, navigate, queryParam.sort_name, queryParam.sort_order, sortNameParam, sortOrderParam]);

  useEffect(() => {
    if (!sellerSetting) {
      return;
    }
    const sellerData = baseHelper.getResponseData(sellerSetting, constant.gql.GET_SELLER_SETTING);
    if (!sellerData) {
      return;
    }

    const { shipping = {} } = sellerData;
    // eslint-disable-next-line no-shadow
    const { isExcludeShippingAmount = false } = shipping || {};

    setIsExcludeShippingAmount(isExcludeShippingAmount);
  }, [sellerSetting]);

  useEffect(() => {
    if (error) {
      setBanner({ isOpen: true, title: errorHelper.parse(error), status: constant.CRITICAL });
    }
  }, [error, setBanner]);

  const orderLineError = baseHelper.getResponseError(orderLinesData, gql.GET_ACCOUNT_LIST);
  useEffect(() => {
    if (orderLineError) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: orderLineError });
    }
  }, [orderLineError, setBanner]);

  useEffect(() => {
    subscribeToMore({
      document: PAYMENT_LIST,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;
        return refetch();
      },
    });
  }, [subscribeToMore, refetch]);

  const responseOrderLinesData = baseHelper.getResponseData(orderLinesData, gql.GET_ACCOUNT_LIST);

  useEffect(() => {
    if (responseOrderLinesData) {
      setPaymentList(responseOrderLinesData && responseOrderLinesData.paymentList);
      setPaymentCount(responseOrderLinesData && responseOrderLinesData.count);
    }
  }, [responseOrderLinesData]);

  const handleSelect = (item) => {
    setSelectedItems(item);
  };

  useEffect(() => {
    if (filterByStatus && filterByStatus.length) {
      baseHelper.setUrl(location, navigate, { filter: filterByStatus, page: 1 });
    } else baseHelper.setUrl(location, navigate, { filter: "", page: 1 });
  }, [filterByStatus]);

  const removeTag = useCallback(
    (tag) => () => {
      setFilterByStatus((previousTags) => previousTags.filter((previousTag) => previousTag !== tag));
    },
    []
  );

  const handleSearchChange = (searchValue) => {
    setSearch(searchValue);
    baseHelper.setUrl(location, navigate, { search: searchValue, page: 1 });
  };
  const handlePageLimitChange = (value) => {
    setPerPage(value);
    baseHelper.setUrl(location, navigate, { perPage: value, page: 1 });
  };
  const handlePageChange = (value) => {
    setPage(value);
    baseHelper.setUrl(location, navigate, { page: value });
  };
  const handleSort = (selected) => {
    setSortValue(selected);
    const sort = selected.split("_");
    if (sort && sort.length === 2) {
      baseHelper.setUrl(location, navigate, { sort_name: sort[0], sort_order: sort[1] });
    }
  };
  const handleQueryClear = () => {
    setSearch(null);
    baseHelper.setUrl(location, navigate, { search: "" });
  };
  const handleBulkAccount = async (key, ids = selectedItems) => {
    if (!ids || !ids.length) {
      return;
    }
    if (bulkModalActive && importError) {
      return;
    }
    try {
      const response = await bulkAccount({ variables: { input: { ids, key } } });
      const responseError = baseHelper.getResponseError(response.data, constant.gql.BULK_ACCOUNT);
      if (responseError) {
        const nextBanner = { isOpen: true, title: responseError, status: CRITICAL };
        if (bulkModalActive) {
          setModalBanner(nextBanner);
        } else {
          setBanner(nextBanner);
        }
      }
      const responseData = baseHelper.getResponseData(response.data, constant.gql.BULK_ACCOUNT);
      if (responseData) {
        setModalBanner({ isOpen: false, title: "", status: "" });
        setBanner({ isOpen: true, title: cms("common.message.success.background"), status: SUCCESS });
        refetch();
        setSelectedItems([]);
        if (bulkModalActive) {
          closeImportModal();
        }
      }
    } catch (exception) {
      const nextBanner = { isOpen: true, title: errorHelper.parse(exception), status: CRITICAL };
      if (bulkModalActive) {
        setModalBanner(nextBanner);
      } else {
        setBanner(nextBanner);
      }
    }
  };

  const parseCsvFields = (line) => {
    let inQuotes = false;
    let field = "";
    const fields = [];
    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      if (char === "\"") {
        const nextChar = line[i + 1];
        if (nextChar === "\"") {
          field += "\"";
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        fields.push(field.trim());
        field = "";
      } else {
        field += char;
      }
    }
    fields.push(field.trim());
    return fields;
  };

  const parseFirstCsvField = (line) => {
    const fields = parseCsvFields(line);
    return fields[0] || "";
  };

  const normalizeId = (value) => {
    let nextValue = value.trim();
    if (nextValue.startsWith("\"") && nextValue.endsWith("\"")) {
      nextValue = nextValue.slice(1, -1);
    }
    return nextValue.trim();
  };

  const parseIdsFromText = (text) => {
    const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (!lines.length) {
      return { ids: [], headerError: "" };
    }
    const headerFields = parseCsvFields(lines[0]).map((value) => normalizeId(value));
    const headerFieldsLower = headerFields.map((value) => value.toLowerCase());
    const orderStatusIndex = headerFieldsLower.indexOf("order status");
    const vendorPaymentStatusIndex = headerFieldsLower.indexOf("vendor payment status");
    if (orderStatusIndex === -1) {
      return { ids: [], headerError: "CSV must include an Order Status column." };
    }
    if (vendorPaymentStatusIndex === -1) {
      return { ids: [], headerError: "CSV must include a Vendor Payment Status column." };
    }
    const firstValue = normalizeId(parseFirstCsvField(lines[0]));
    const startIndex = firstValue.toLowerCase() === "id" ? 1 : 0;
    const idSet = new Set();
    for (let i = startIndex; i < lines.length; i += 1) {
      const rowFields = parseCsvFields(lines[i]).map((value) => normalizeId(value));
      const orderStatusValue = rowFields[orderStatusIndex] || "";
      const vendorPaymentStatusValue = rowFields[vendorPaymentStatusIndex] || "";
      const normalizedStatus = orderStatusValue.toLowerCase();
      if (!normalizedStatus || normalizedStatus === "new") {
        return { ids: [], headerError: "Please accept the order line item first." };
      }
      if (["cancelled"].includes(normalizedStatus)) {
        return { ids: [], headerError: "Cancelled lineitems payment cannot be processed." };
      }
      if (!["accepted", "shipped", "closed"].includes(normalizedStatus)) {
        return { ids: [], headerError: "Cancelled lineitems payment cannot be processed." };
      }
      if (vendorPaymentStatusValue.toLowerCase() !== "unpaid") {
        return { ids: [], headerError: "This vendor is already paid." };
      }
      const rawValue = rowFields[0] || "";
      if (rawValue) {
        idSet.add(rawValue);
      }
    }
    return { ids: Array.from(idSet), headerError: "" };
  };

  const closeImportModal = () => {
    if (setBulkModalActive) {
      setBulkModalActive(false);
    }
    resetImportState();
  };

  useEffect(() => {
    if (!bulkModalActive) {
      resetImportState();
    }
  }, [bulkModalActive]);

  const handleImportValues = (values) => {
    setModalBanner({ isOpen: false, title: "", status: "" });
    setImportValues(values);
    if (!values || !values.data) {
      setImportedIds([]);
      setImportFileName("");      
      setImportError("");
      return;
    }
    const { ids: parsedIds, headerError } = parseIdsFromText(String(values.data));
    if (headerError) {
      setImportError(headerError);
      setImportedIds([]);
      setModalBanner({ isOpen: true, title: headerError, status: CRITICAL });
      return;
    }
    if (!parsedIds.length) {
      setImportError("No IDs found in the first column.");
      setImportedIds([]);
      return;
    }
    setImportError("");
    setImportedIds(parsedIds);
    setImportFileName(values.fileName || "");
  };

  const bulkActionButton = (key) => {
    let disabled = true;
    if (selectedItems && selectedItems.length) {
      selectedItems.forEach((paymentId) => {
        const index = paymentListData.findIndex((paymentList) => {
          const { _id: id } = paymentList;
          return id === paymentId;
        });
        if (index === -1) {
          return;
        }
        if ((key === PAYPAL || key === STRIPE) && paymentListData[index].accountingStatus === UNPAID) {
          disabled = false;
        }
        if (key === DISPUTE && paymentListData[index].accountingStatus !== DISPUTE) {
          disabled = false;
        }
        if (key === PAID && paymentListData[index].accountingStatus === UNPAID) {
          disabled = false;
        }
      });
    }
    return disabled;
  };

  const bulkActions = [
    {
      content: cms("label.payPaypal"),
      onAction: () => handleBulkAccount(PAIDVIAPAYPAL),
      disabled: bulkActionButton(PAYPAL),
    },
    {
      content: cms("label.payStripe"),
      onAction: () => handleBulkAccount(PAIDVIASTRIPE),
      disabled: bulkActionButton(STRIPE),
    },
    {
      content: cms("label.markDispute"),
      onAction: () => handleBulkAccount(DISPUTE),
      disabled: bulkActionButton(DISPUTE),
    },
    {
      content: cms("label.markPaid"),
      onAction: () => handleBulkAccount(PAID),
      disabled: bulkActionButton(PAID),
    },
  ];

  const handleTaggedWithRemove = (clearFilter = false, key) => {
    const clearParams = key === constant.FILTER_BY_STATUS ? { list_filter: "", page: 1 } : { list_search: "", page: 1 };
    if (clearFilter) {
      clearParams.list_filter = "";
      if (key) {
        switch (key) {
          case constant.TAGGED_WITH:
            return [setTaggedWith(""), baseHelper.setUrl(location, navigate, clearParams)];
          case constant.FILTER_BY_STATUS:
            return [setFilterByStatus(null), baseHelper.setUrl(location, navigate, clearParams)];
          case constant.CLEAR_ALL:
            return [
              setTaggedWith(""),
              setSelectedFilter(""),
              setFilterByStatus(null),
              baseHelper.setUrl(location, navigate, clearParams),
            ];
          default:
            return null;
        }
      }
    }
    if (key === constant.FILTER_BY_STATUS) {
      setFilterByStatus(null);
    } else {
      clearParams.list_filter = "";
      setTaggedWith("");
      setSelectedFilter("");
    }

    baseHelper.setUrl(location, navigate, clearParams);
    return null;
  };

  const handleSelectedFilterRemove = () => {
    setSelectedFilter("");
    setTaggedWith("");
    baseHelper.setUrl(location, navigate, { list_search: "", list_filter: "", page: 1 });
  };

  const handleClearAll = () => {
    handleSelectedFilterRemove();
    handleTaggedWithRemove(true, constant.CLEAR_ALL);
    setFilterByStatus(null);
  };

  let tagMarkup = [];
  if (filterByStatus && filterByStatus.length) {
    tagMarkup =
      filterByStatus &&
      filterByStatus.map((option) => (
        <Tag key={option} onRemove={removeTag(option)}>
          {option === constant.FORECAST
            ? cms("label.forecast")
            : option === constant.DUE
              ? cms("label.duePayment")
              : option === constant.DISPUTE
                ? cms("label.dispute")
                : baseHelper.ucFirst(option)}
        </Tag>
      ));
  }

  const disambiguateLabel = (key, value) => {
    switch (key) {
      case constant.TAGGED_WITH:
        return `${value}`;
      case constant.SELECTED_FILTER:
        return cms(`label.${value}`) || value;
      case constant.FILTER_BY_STATUS:
        return <div className="remove_tag">{tagMarkup}</div>;
      default:
        return value;
    }
  };

  const isEmpty = (value) => {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    return value === "" || value == null;
  };

  const appliedFilterArrObj = [];

  if (!isEmpty(taggedWith)) {
    appliedFilterArrObj.push({
      key: constant.TAGGED_WITH,
      label: disambiguateLabel(constant.TAGGED_WITH, taggedWith),
      onRemove: () => handleTaggedWithRemove(false),
    });
  }

  if (!isEmpty(selectedFilter)) {
    appliedFilterArrObj.push({
      key: constant.SELECTED_FILTER,
      label: disambiguateLabel(constant.SELECTED_FILTER, selectedFilter),
      onRemove: () => handleTaggedWithRemove(false),
    });
  }

  if (!isEmpty(filterByStatus)) {
    appliedFilterArrObj.push({
      key: constant.FILTER_BY_STATUS,
      label: disambiguateLabel(constant.FILTER_BY_STATUS, filterByStatus),
      onRemove: () => handleTaggedWithRemove(false, constant.FILTER_BY_STATUS),
    });
  }
  const appliedFilters = appliedFilterArrObj;

  const filterOptions = [{ label: cms("label.brandName"), value: constant.VENDOR }];

  const filterByStatusOption = [
    {
      label: cms("label.all"),
      value: "all",
    },
    {
      label: cms("label.forecast"),
      value: "forecast",
    },
    {
      label: cms("label.duePayment"),
      value: "due",
    },
    {
      label: cms("label.pay"),
      value: "paid",
    },
    {
      label: cms("label.dispute"),
      value: "dispute",
    },
  ];

  const handleAction = (value) => {
    setFilterByStatus(value);
    baseHelper.setUrl(location, navigate, { filter: value, page: 1 });
    if (!value.length) {
      baseHelper.setUrl(location, navigate, { filter: "all", page: 1 });
    }
  };

  const filters = [
    {
      key: "taggedWith",
      label: cms("label.filter"),
      // label: "Filter by",
      filter: (
        <MoreFilters
          taggedWith={taggedWith}
          setTaggedWith={setTaggedWith}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          filterOptions={filterOptions}
        />
      ),
    },
    {
      key: constant.FILTER_BY_STATUS,
      label: cms("common.label.filterByStatus"),
      filter: (
        // <Popover active={popoverActive} activator={activator} onClose={togglePopoverActive}>
        //   <OptionList
        //     fullWidth
        //     allowMultiple
        //     onChange={(value) => handleAction(value)}
        //     options={filterByStatusOption}
        //     selected={filterByStatus || []}
        //   />
        // </Popover>

        <ChoiceList
          title={cms("common.label.filterByStatus")}
          titleHidden
          choices={filterByStatusOption}
          selected={filterByStatus || []}
          onChange={(value) => handleAction(value)}
          allowMultiple
        />
      ),
    },
  ];

  return (
    <>
      <Modal
        open={bulkModalActive}
        onClose={closeImportModal}
        title={`Bulk Mark as Paid`}
        primaryAction={{
          content: cms("label.markPaid"),
          onAction: () => handleBulkAccount(PAID, importedIds),
          disabled: !importedIds.length || !!importError,
        }}
        secondaryActions={[
          {
            content: "Close",
            onAction: closeImportModal,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {modalBanner.isOpen && (
              <Banner
                isOpen
                status={modalBanner.status}
                title={modalBanner.title}
                onDismiss={() => {
                  setModalBanner({ isOpen: false, title: "", status: "" });
                  setImportError("");
                  setImportValues({});
                }}
              />
            )}
            <FileUpload setValue={handleImportValues} values={importValues} isLabelVisible={false} />
            <BlockStack >
              <Text>
              {importedIds.length ? <span>{`${importedIds.length} Lineitems ready`}</span> : null}
              </Text>
            </BlockStack>
          </BlockStack>
        </Modal.Section>
      </Modal>
      <ResourceList
        items={paymentListData}
        idForItem={(orderLine) => {
          const { _id: id } = orderLine;
          return id;
        }}
        renderItem={renderItem}
        loading={loading || orderLoading || sellerSettingLoading}
        queryValue={search}
        onQueryClear={handleQueryClear}
        onQueryChange={handleSearchChange}
        sortOptions={resourceData.sortOptions}
        sortValue={sortValue}
        onSortChange={handleSort}
        selectedItems={selectedItems}
        onSelectionChange={handleSelect}
        bulkActions={bulkActions}
        filters={filters}
        appliedFilters={appliedFilters}
        handleClearAll={handleClearAll}
        selectable
        count={paymentCount}
        page={page}
        perPage={perPage}
        setPage={handlePageChange}
        setPopoverDisable={setPopoverDisable}
        setPerPage={handlePageLimitChange}
      />
    </>
  );
};
PaymentList.propTypes = listProp.type;
export default PaymentList;
